import type { Vec2 } from '../../geo/types';
import type { WaterEdge } from '../../site/types';

/**
 * A water edge run with the water on its right, as the quays draw it (terrain/quays.ts). `startU` is how
 * far its chain has run before it, so a revetment's posts and planks keep their rhythm from one edge of
 * a bank to the next.
 */
export interface Bank {
  readonly index: number;
  readonly edge: WaterEdge;
  readonly a: Vec2;
  readonly b: Vec2;
  readonly direction: Vec2;
  readonly length: number;
  readonly startU: number;
  /** The bank that ends where this one starts, and the one that starts where it ends. */
  readonly previous: number | null;
  readonly next: number | null;
}

type Oriented = Omit<Bank, 'startU' | 'previous' | 'next'>;

function oriented(edge: WaterEdge, index: number): Oriented {
  const { a, b } = edge.segment;
  const [dx, dy] = [b[0] - a[0], b[1] - a[1]];
  const waterOnRight = dy * edge.towardWater[0] - dx * edge.towardWater[1] > 0;
  const [from, to] = waterOnRight ? [a, b] : [b, a];
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  const direction: Vec2 = [(to[0] - from[0]) / length, (to[1] - from[1]) / length];
  return { index, edge, a: from, b: to, direction, length };
}

const keyOf = (p: Vec2) => `${p[0]},${p[1]}`;

/** Each chain from its first bank on: a bank nothing runs into first, then what is left of rings. */
function chainStarts(banks: readonly Oriented[], next: ReadonlyMap<number, number>): number[] {
  const entered = new Set(next.values());
  const open = banks.filter((b) => !entered.has(b.index)).map((b) => b.index);
  return [...open, ...banks.map((b) => b.index)];
}

export function banksOf(waterEdges: readonly WaterEdge[]): readonly Bank[] {
  const banks = waterEdges.map(oriented);
  const byStart = new Map(banks.map((bank) => [keyOf(bank.a), bank.index] as const));
  const next = new Map<number, number>();
  for (const bank of banks) {
    const after = byStart.get(keyOf(bank.b));
    if (after !== undefined && after !== bank.index) next.set(bank.index, after);
  }
  const startU = new Map<number, number>();
  for (const first of chainStarts(banks, next)) {
    let [at, u]: [number | undefined, number] = [first, 0];
    while (at !== undefined && !startU.has(at)) {
      startU.set(at, u);
      u += banks[at]!.length;
      at = next.get(at);
    }
  }
  const previous = new Map([...next].map(([from, to]) => [to, from] as const));
  return banks.map((bank) => ({
    ...bank,
    startU: startU.get(bank.index)!,
    previous: previous.get(bank.index) ?? null,
    next: next.get(bank.index) ?? null,
  }));
}
